import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

interface NavItem {
  path: string;
  labelKey: string;
  icon: string;
}

interface NavGroup {
  labelKey: string;
  icon: string;
  items: NavItem[];
}

type NavEntry = NavItem | NavGroup;

const isGroup = (entry: NavEntry): entry is NavGroup => 'items' in entry;

const navEntries: NavEntry[] = [
  { path: '/dashboard', labelKey: 'sidebar.dashboard', icon: 'dashboard' },
  { path: '/brands', labelKey: 'sidebar.brands', icon: 'storefront' },
  { path: '/packages', labelKey: 'sidebar.packages', icon: 'inventory_2' },
  {
    labelKey: 'sidebar.usersGroup',
    icon: 'group',
    items: [
      { path: '/users', labelKey: 'sidebar.users', icon: 'person' },
      { path: '/roles', labelKey: 'sidebar.roles', icon: 'shield' },
    ],
  },
  { path: '/tickets', labelKey: 'sidebar.tickets', icon: 'confirmation_number' },
  { path: '/billing', labelKey: 'sidebar.billing', icon: 'credit_card' },
  { path: '/system-logs', labelKey: 'sidebar.systemLogs', icon: 'history' },
  { path: '/settings', labelKey: 'sidebar.settings', icon: 'settings' },
];

const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const initials = user?.firstName && user?.lastName
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : user?.fullName?.charAt(0)?.toUpperCase() || 'A';

  const renderNavLink = (item: NavItem, nested = false) => (
    <NavLink
      key={item.path}
      to={item.path}
      className={({ isActive }) =>
        isActive
          ? `flex items-center gap-3 ${nested ? 'pl-10 pr-3' : 'px-3'} py-2.5 rounded-lg bg-primary/10 text-primary active-glow border border-primary/20 cursor-pointer`
          : `sidebar-item flex items-center gap-3 ${nested ? 'pl-10 pr-3' : 'px-3'} py-2.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-all cursor-pointer`
      }
    >
      {({ isActive }) => (
        <>
          <span className="material-symbols-outlined text-xl">{item.icon}</span>
          <p className={`text-sm ${isActive ? 'font-bold' : 'font-medium'}`}>
            {t(item.labelKey)}
          </p>
        </>
      )}
    </NavLink>
  );

  const renderGroup = (group: NavGroup) => {
    const isOpen = openGroups[group.labelKey] ?? false;
    const hasActiveChild = group.items.some((item) => location.pathname === item.path);

    return (
      <div key={group.labelKey}>
        <button
          onClick={() => toggleGroup(group.labelKey)}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
            hasActiveChild
              ? 'text-primary'
              : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
          }`}
        >
          <span className={`material-symbols-outlined text-xl ${hasActiveChild ? 'text-primary' : ''}`}>
            {group.icon}
          </span>
          <p className={`text-sm flex-1 text-left ${hasActiveChild ? 'font-bold' : 'font-medium'}`}>
            {t(group.labelKey)}
          </p>
          <span
            className={`material-symbols-outlined text-lg transition-transform ${
              isOpen || hasActiveChild ? 'rotate-180' : ''
            }`}
          >
            expand_more
          </span>
        </button>

        {(isOpen || hasActiveChild) && (
          <div className="flex flex-col gap-1 mt-1">
            {group.items.map((item) => renderNavLink(item, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className="w-64 border-r border-slate-200 flex flex-col h-screen bg-white transition-all">
      {/* Brand */}
      <div className="p-6">
        <img src="/logo.png" alt="FoxygenSeo" className="h-15 w-auto" />
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 space-y-1 mt-4 overflow-y-auto">
        {navEntries.map((entry) =>
          isGroup(entry) ? renderGroup(entry) : renderNavLink(entry)
        )}
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-card-border">
        <div
          onClick={handleLogout}
          className="flex items-center gap-3 p-2 rounded-lg hover:bg-red-50 cursor-pointer group"
        >
          <div className="size-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm">
            {initials}
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <p className="text-sm font-bold leading-none truncate text-slate-800">
              {user?.fullName || 'Admin User'}
            </p>
            <p className="text-xs text-slate-400 mt-1 truncate">
              {user?.role?.name || 'Super Admin'}
            </p>
          </div>
          <span className="material-symbols-outlined text-slate-300 text-lg group-hover:text-red-400 transition-colors">logout</span>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
